import type { Department } from "../../contexts/AuthProvider";
import { useAuth } from "../../contexts/AuthProvider";
import { useChosenDepartment } from "../../contexts/ChosenDepartment/hooks";
import { DepartmentType } from "../../utils/models";
import { isBedSideNurse } from "../../utils/utilFunctions";

/**
 * PBJ is filed for skilled nursing, so a building's PBJ is its SNF-type departments (nursing, Dietary,
 * Housekeeping). An assisted-living or hospital department is never part of one, and the backend
 * refuses PBJ reads from it — so the page is not offered there at all.
 */
export const isPbjDepartment = (department: Pick<Department, "type">): boolean => department.type === DepartmentType.SNF;

/** Whether the PBJ Model tab shows: the flag is on, the user is not bedside staff, and the department is SNF-type. */
export const canShowPbjModel = (isFlagEnabled: boolean, role: string, department: Pick<Department, "type">): boolean =>
	isFlagEnabled && !isBedSideNurse(role) && isPbjDepartment(department);

export type PbjModelRouteAccess = {
	/** The chosen department is SNF-type. Always false while `loading`. */
	allowed: boolean;
	/** On a cold load the chosen department is picked after the first render; until then its type is unknown. */
	loading: boolean;
};

/** The PBJ Model route's department gate, read from the department the app already holds (no request). */
export const usePbjModelRouteAccess = (): PbjModelRouteAccess => {
	const { userInfo } = useAuth();
	const { chosenDepartment } = useChosenDepartment();
	// A user with no department never gets one chosen, so there is nothing to wait for — just no access.
	if (chosenDepartment.department_id === "") return { allowed: false, loading: !!userInfo.departments?.length };
	return { allowed: isPbjDepartment(chosenDepartment), loading: false };
};
